import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export interface IResponseHandlerOptions<T> {
  groupName?: string;
  onSuccess?: (value: T) => void;
  onError?: (status: number) => void;
  onUnauthorized?: () => void;
  unauthorizedPath?: string;
  unauthorizedToastError?: string;
  onMethodNotAllowed?: () => void;
  methodNotAllowedPath?: string;
  methodNotAllowedToastError?: string;
  onNotFound?: () => void;
  notFoundPath?: string;
  notFoundToastError?: string;
  onInternalServerError?: () => void;
  internalServerErrorPath?: string;
  internalServerErrorToastError?: string;
}

export const useResponseHandler = () => {
  const { t } = useTranslation();

  const responseStreamHandler = async (response: Response) => {
    if (!response.ok) throw new Error('Błąd pobierania pliku');

    const blob = await response.blob();

    return blob;
  };
  const responseHandler = async <T>(
    response: Response,
    options?: IResponseHandlerOptions<T>
  ): Promise<T> => {
    console.group(options?.groupName ?? (response.ok ? 'success' : 'error'));

    console.log('Response status:', response.status);

    if (response.status >= 500) {
      toast.error(
        options?.internalServerErrorToastError ??
          t('service-message.internal-server-error', { code: response.status }),
        { id: 'internal-server-error' }
      );
      options?.onInternalServerError && options?.onInternalServerError();
      console.groupEnd();
      throw new Error(
        t('service-message.internal-server-error', { code: response.status })
      );
    }

    const responseBody = await response.text();

    console.log(
      'Response body:',
      responseBody.length ? JSON.parse(responseBody) : responseBody
    );

    console.groupEnd();

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(t('service-message.unauthorized'));
      }
      if (response.status === 404) {
        if (!options?.onNotFound) {
          toast.error(
            options?.notFoundToastError ?? t('service-message.not-found'),
            { id: 'not-found' }
          );
          throw new Error(t('service-message.not-found'));
        }
        options?.onNotFound && options?.onNotFound();
      }
      if (response.status === 405) {
        if (!options?.onMethodNotAllowed) {
          toast.error(
            options?.methodNotAllowedToastError ??
              t('service-message.method-not-allowed'),
            { id: 'method-not-allowed' }
          );
        }
        options?.onMethodNotAllowed && options?.onMethodNotAllowed();
        throw new Error(t('service-message.method-not-allowed'));
      }

      if (options?.onError) {
        options.onError(response.status);
      }
      throw new Error(responseBody);
    }

    const data = JSON.parse(responseBody);

    options?.onSuccess && options.onSuccess(data);

    if ('value' in data) return data.value;

    if ('response' in data) return data.response;

    return data;
  };

  return { responseHandler, responseStreamHandler };
};
